import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Target, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TargetSection({ id, label, noun, targets, setTargets, options }) {
  const [draft, setDraft] = useState('');
  const set = (k, f, v) => setTargets((t) => ({ ...t, [k]: { ...(t[k] || {}), [f]: v } }));
  const rows = Object.keys(targets).sort();
  const unused = options.filter((o) => !targets[o]);
  return (
    <div data-testid={`targets-section-${id}`}>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">{label}</p>
      {!rows.length && <p className="text-[11px] text-slate-500 mb-1" data-testid={`targets-empty-${id}`}>No {noun} targets yet.</p>}
      {rows.length > 0 && (
        <table className="w-full text-xs mb-1" data-testid={`targets-table-${id}`}>
          <thead><tr className="text-[10px] uppercase text-slate-400 border-b border-slate-200"><th className="text-left py-1 font-medium">{noun}</th><th className="text-left font-medium">ARR target (A$)</th><th className="text-left font-medium">Margin target %</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r} className="border-b border-slate-100" data-testid={`target-row-${id}-${r}`}>
                <td className="py-1 text-slate-700 font-medium">{r}</td>
                <td className="py-1 pr-2"><Input type="number" min="0" step="1000" className="h-7 text-xs w-40" value={targets[r]?.arr_aud ?? ''} onChange={(e) => set(r, 'arr_aud', e.target.value)} data-testid={`target-arr-${id}-${r}`} /></td>
                <td className="py-1 pr-2"><Input type="number" min="0" max="100" step="0.5" className="h-7 text-xs w-24" value={targets[r]?.margin_pct ?? ''} onChange={(e) => set(r, 'margin_pct', e.target.value)} data-testid={`target-margin-${id}-${r}`} /></td>
                <td className="py-1 text-right"><button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => setTargets((t) => { const n = { ...t }; delete n[r]; return n; })} title="Remove" data-testid={`target-remove-${id}-${r}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center gap-2">
        <Input list={`target-opts-${id}`} className="h-8 text-xs w-56" placeholder={`${noun} name`} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`target-new-${id}`} />
        <datalist id={`target-opts-${id}`}>{unused.map((o) => <option key={o} value={o} />)}</datalist>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!draft.trim() || !!targets[draft.trim()]} onClick={() => { set(draft.trim(), 'arr_aud', ''); setDraft(''); }} data-testid={`target-add-${id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add {noun.toLowerCase()}</Button>
      </div>
    </div>
  );
}

/** Admin → per-region and per-rep ARR / margin targets shown as progress bars on the Deal Overview leaderboards. */
export function RegionalTargetsCard() {
  const [targets, setTargets] = useState(null);
  const [repTargets, setRepTargets] = useState({});
  const [opts, setOpts] = useState({ regions: [], reps: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API_URL}/api/insights/deal-overview/targets`, { withCredentials: true }).then((r) => { setTargets(r.data.targets); setRepTargets(r.data.rep_targets || {}); setOpts({ regions: r.data.regions, reps: r.data.reps || [] }); }).catch(() => setTargets({})); }, []);
  if (!targets) return <Card className="border-slate-200"><CardContent className="p-5 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading targets…</CardContent></Card>;
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/insights/deal-overview/targets`, { targets, rep_targets: repTargets }, { withCredentials: true });
      setTargets(r.data.targets); setRepTargets(r.data.rep_targets || {});
      toast.success(`Targets saved — ${Object.keys(r.data.targets).length} region(s), ${Object.keys(r.data.rep_targets || {}).length} rep(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  return (
    <Card className="border-slate-200" data-testid="regional-targets-card">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2"><Target className="w-4 h-4 text-[#4ABFCF]" /> Regional & rep targets
          <span className="text-xs font-normal text-slate-500">ARR (A$, full open pipeline) and term margin % · shown as progress on the Deal Overview leaderboards</span></CardTitle>
        <Button size="sm" className="h-8 bg-[#0F2040] hover:bg-[#1a3260]" onClick={save} disabled={saving} data-testid="targets-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save</Button>
      </CardHeader>
      <CardContent className="space-y-4 text-xs">
        <TargetSection id="region" label="By region" noun="Region" targets={targets} setTargets={setTargets} options={opts.regions} />
        <TargetSection id="rep" label="By sales rep" noun="Rep" targets={repTargets} setTargets={setRepTargets} options={opts.reps} />
        <p className="text-[10px] text-slate-400">Leave a field blank to skip that target. Targets apply to the full open pipeline (not per year). Rep names must match the sales lead / owner name shown on the leaderboard.</p>
      </CardContent>
    </Card>
  );
}
